<template>
  <v-dialog :value="true" dark max-width="620" scrollable @input="!$event && $emit('close')">
    <v-card>
      <v-card-title class="text-h5 font-weight-bold">Plan von Person übernehmen</v-card-title>
      <v-card-text>
        <user-search dark outlined disabled rights="tp_create" label="Neuer Plan für" :value="targetCustomerId" />
        <p>Suche die Person, deren Trainingsplan du als Vorlage verwenden möchtest.</p>
        <user-search dark filled rounded rights="tp_create" label="Plan von Person" :value="sourceCustomerId" @update="loadPlans" />
        <div v-if="loading" class="text-center py-6" role="status">
          <v-progress-circular indeterminate color="primary" />
          <div class="mt-2">Trainingspläne werden geladen …</div>
        </div>
        <v-alert v-else-if="error" type="error" text>
          {{ error }}
          <v-btn small text @click="loadPlans(sourceCustomerId)">Erneut laden</v-btn>
        </v-alert>
        <p v-else-if="sourceCustomerId && !plans.length" class="py-4">Für diese Person sind keine kopierbaren Trainingspläne vorhanden.</p>
        <v-radio-group v-else-if="plans.length" v-model="selectedPlanId" class="mt-4">
          <v-card v-for="plan in plans" :key="plan._id" outlined class="mb-3 pa-3">
            <v-radio :value="plan._id" :label="plan.title || plan.splits.text || 'Trainingsplan'" />
            <div class="ml-8 caption">
              {{ plan.deleted ? 'Archiviert' : 'Aktiv' }} · {{ formatDate(plan.createdAt) }}
              <div v-for="(split, index) in plan.splits.workouts" :key="index" class="mt-1">
                {{ split.title || 'Trainingstag ' + (index + 1) }} · {{ exerciseCount(split) }} Übungen
              </div>
            </div>
          </v-card>
        </v-radio-group>
        <v-alert v-if="selectedPlan" type="info" text class="mb-0">
          Übungen, Sätze, Gewichte und Geräteeinstellungen werden kopiert. Prüfe sie anschliessend für den Zielkunden.
          Der ursprüngliche Plan und sein Trainingsverlauf bleiben unverändert.
        </v-alert>
        <v-alert v-if="selectedPlan && replacesExercises" type="warning" text class="mt-3 mb-0">
          Die Übungen im aktuellen Entwurf werden durch den ausgewählten Plan ersetzt.
        </v-alert>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-btn text @click="$emit('close')">Abbrechen</v-btn>
        <v-spacer />
        <v-btn color="primary" :disabled="!selectedPlan || loading || !!error" @click="confirmSelection">Übernehmen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "../../api"
import userSearch from "../../components/userSearch.vue"

export default {
  name: "WorkoutPlanCopyDialog",
  components: { userSearch },
  props: {
    targetCustomerId: { required: true },
    replacesExercises: { type: Boolean, default: false },
  },
  data() {
    return { sourceCustomerId: null, plans: [], selectedPlanId: null, loading: false, error: "", requestSequence: 0 }
  },
  computed: {
    selectedPlan() {
      return this.plans.find((plan) => plan._id === this.selectedPlanId) || null
    },
  },
  beforeDestroy() {
    this.requestSequence += 1
  },
  methods: {
    async loadPlans(customerId) {
      const sequence = ++this.requestSequence
      this.sourceCustomerId = customerId
      this.plans = []
      this.selectedPlanId = null
      this.error = ""
      this.loading = !!customerId
      if (!customerId) return
      try {
        const plans = await api.workoutApi.fetchCustomerWorkouts(customerId, "trainer")
        if (sequence !== this.requestSequence) return
        this.plans = (Array.isArray(plans) ? plans : []).filter(
          (plan) => plan._id && plan.splits && Array.isArray(plan.splits.workouts) && plan.splits.workouts.length,
        )
      } catch (error) {
        if (sequence !== this.requestSequence) return
        // This endpoint returns 404 when the customer has no V2 plans.
        if (!error.response || error.response.status !== 404) {
          this.error = error.response && error.response.status === 403
            ? "Du hast keine Berechtigung, diese Trainingspläne zu laden."
            : "Die Trainingspläne konnten nicht geladen werden."
        }
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    confirmSelection() {
      if (this.selectedPlan && !this.loading && !this.error) this.$emit("select", this.selectedPlan)
    },
    formatDate(value) {
      const date = new Date(value)
      return value && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("de-CH") : "Datum unbekannt"
    },
    exerciseCount(split) {
      return (split.exercises || []).reduce((count, exercise) => count + (exercise.isSuperset ? (exercise.exercises || []).length : 1), 0)
    },
  },
}
</script>
